import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import assert from 'assert';
import React from 'react';

import {ClientId} from 'notebook/ELNConstants';
import {
  AugmentNotePartsContext,
  updateTableNotePartsFromTableMap,
} from 'notebook/mediocre/core-ui/augmentNoteParts';
import getNotePartType from 'notebook/notes/util/getNotePartType';
import toColumnWidth from 'notebook/structured-tables/columns/toColumnWidth';
import {makeStructuredTableColumn} from 'notebook/structured-tables/util/StructuredTableColumnUtil';
import {extractTables} from 'notebook/tables/augmentNotePartTablesWithValues';
import {insertColumnsLeft, deleteColumns} from 'notebook/tables/excess/ops/TableOps';
import {InsertableColumnInfo, TableColumnType} from 'notebook/tables/TableConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {GenericStructuredTable, NoteLinkedObjectType, NotePart} from 'notebook/util/NoteUtilConstants';

export interface ProcedureConditionReplicateCellDisplayProps {
  conditionName: string;
  replicateNumber: number;
}

export function ProcedureConditionReplicateCellDisplay({
  conditionName,
  replicateNumber,
}: ProcedureConditionReplicateCellDisplayProps): JSX.Element {
  return (
    <div className={css(styles.procedureConditionReplicateCellDisplay)}>
      {/* Add a tooltip to the Condition name since it can often become ellipsized */}
      <span className={css(styles.conditionName)} title={conditionName}>
        {conditionName}
      </span>
      <span className={css(styles.replicateNumber)}>{replicateNumber}</span>
    </div>
  );
}

const styles = StyleSheet.create({
  procedureConditionReplicateCellDisplay: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  conditionName: {
    flex: 1,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  replicateNumber: {
    flex: 0,
    marginLeft: 8,
    color: Color.GRAY_600,
  },
});

export const PROCEDURE_CONDITION_REPLICATE_COLUMN_INFO: InsertableColumnInfo = {
  columnLabels: 'Condition - Replicate',
  columnConstraints: {
    structuredTableColumn: makeStructuredTableColumn({
      columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES,
      name: 'Condition - Replicate',
      isRequired: true,
    }),
  },
  widths: toColumnWidth({columnType: TableColumnType.PROCEDURE_CONDITION_REPLICATES}),
};

type InsertOrRemoveProcedureConditionReplicateColumnContext = Pick<
  AugmentNotePartsContext,
  'fileId' | 'isProcedureMultibatchExecution'
>;

/** For all inserted StructuredTables in a given set of NoteParts, insert a Condition-Replicate column
 * if we're in Procedure Multi-Batch Execution.
 *
 * If we're not in Multi-Batch, remove a Condition-Replicate column if one happens to exist (like when
 * copying tables from Multi-Batch to somewhere else).
 *
 * We must do this with access to all Note Parts because we want to insert the column into the front of the
 * table which could shift formulas in pasted tables and subtemplates.
 */
export function insertOrRemoveProcedureConditionReplicateColumn(
  noteParts: Array<NotePart>,
  {fileId, isProcedureMultibatchExecution}: InsertOrRemoveProcedureConditionReplicateColumnContext
): Array<NotePart> {
  let {fullTableMap: tableMap} = extractTables(noteParts, fileId, []);
  for (const notePart of noteParts) {
    const notePartType = getNotePartType(notePart);
    // TODO(BNCH-115346): Mixture Prep tables need to also be updated so we need to dive into NoteLinkedGroups
    // as well
    if (notePartType !== NoteLinkedObjectType.STRUCTURED_TABLE) {
      continue;
    }
    assert(notePart.item, 'StructuredTable NotePart must have an item');
    const structuredTable = notePart.item as GenericStructuredTable<ClientId>;
    const table = tableMap[structuredTable.name];
    const existingConditionReplicateColumnIndex = table.columnConstraints.findIndex(
      (columnConstraint) =>
        columnConstraint?.structuredTableColumn?.columnType === TableColumnType.PROCEDURE_CONDITION_REPLICATES
    );
    const singleInputBoundaryIndex = TableUtil.findSingleInputBoundaryIndex(table);
    const hasExistingConditionReplicateColumn = existingConditionReplicateColumnIndex !== -1;
    if (isProcedureMultibatchExecution && !hasExistingConditionReplicateColumn) {
      tableMap = insertColumnsLeft(
        tableMap,
        table.name,
        singleInputBoundaryIndex,
        singleInputBoundaryIndex,
        PROCEDURE_CONDITION_REPLICATE_COLUMN_INFO
      ).tables;
    } else if (!isProcedureMultibatchExecution && hasExistingConditionReplicateColumn) {
      // If not in Multi-Batch Execution, remove the Condition-Replicate column if one exists
      tableMap = deleteColumns(
        tableMap,
        table.name,
        existingConditionReplicateColumnIndex,
        existingConditionReplicateColumnIndex
      ).tables;
    }
  }

  return updateTableNotePartsFromTableMap(tableMap, noteParts);
}
